import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import {
  Users,
  Shield,
  Settings,
  FileText,
  Image,
  Award,
  ArrowRight,
  BookOpen,
  Camera,
  Loader2
} from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';

interface ManagementCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  color: {
    bg: string;
    iconBg: string;
    text: string;
    button: string;
    buttonHover: string;
  };
}

const ManagementCard: React.FC<ManagementCardProps> = ({ title, description, icon, href, color }) => {
  const navigate = useNavigate();

  return (
    <Card
      className={`${color.bg} text-white border-0 shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden p-0`}
      onClick={() => navigate(href)}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 ${color.iconBg} rounded-xl`}>
            {icon}
          </div>
        </div>
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className={`text-sm ${color.text} mb-4`}>{description}</p>
        <Button
          className={`w-full ${color.button} ${color.buttonHover}`}
          onClick={(e) => {
            e.stopPropagation();
            navigate(href);
          }}
        >
          Manage
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
};

interface Stats {
  users: number;
  materials: number;
  events: number;
  teamMembers: number;
  successStories: number;
}

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({ users: 0, materials: 0, events: 0, teamMembers: 0, successStories: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      try {
        // Fetch all stats in parallel
        const [usersRes, materialsRes, eventsRes, teamRes, storiesRes] = await Promise.all([
          fetch(`${config.api.baseUrl}/api/user/admin/users`, { headers }).catch(() => null),
          fetch(`${config.api.baseUrl}/api/materials`).catch(() => null),
          fetch(`${config.api.baseUrl}/api/events`).catch(() => null),
          fetch(`${config.api.baseUrl}/api/team`).catch(() => null),
          fetch(`${config.api.baseUrl}/api/achievements`).catch(() => null),
        ]);

        let usersCount = 0;
        let materialsCount = 0;
        let eventsCount = 0;
        let teamCount = 0;
        let storiesCount = 0;

        if (usersRes?.ok) {
          const data = await usersRes.json();
          usersCount = data.users?.length || data.length || 0;
        }

        if (materialsRes?.ok) {
          const data = await materialsRes.json();
          materialsCount = data.materials?.length || 0;
        }

        if (eventsRes?.ok) {
          const data = await eventsRes.json();
          eventsCount = data.events?.length || 0;
        }

        if (teamRes?.ok) {
          const data = await teamRes.json();
          teamCount = data.members?.length || 0;
        }

        if (storiesRes?.ok) {
          const data = await storiesRes.json();
          storiesCount = data.stories?.length || 0;
        }

        setStats({
          users: usersCount,
          materials: materialsCount,
          events: eventsCount,
          teamMembers: teamCount,
          successStories: storiesCount,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (!user) {
    return null;
  }

  if (user.role !== 'admin') {
    return (
      <>
        <SEO title="Access Denied" />
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Card className="max-w-md overflow-hidden p-0">
              <div className="bg-gradient-to-r from-red-100 to-orange-100 px-6 py-5">
                <CardTitle className="flex items-center gap-2">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Shield className="h-5 w-5 text-red-600" />
                  </div>
                  Access Denied
                </CardTitle>
              </div>
              <CardContent className="p-6">
                <p className="text-muted-foreground mb-4">
                  You don't have permission to access the admin panel.
                </p>
                <Button onClick={() => navigate('/dashboard')}>
                  Back to Dashboard
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  const managementItems: ManagementCardProps[] = [
    {
      title: 'Manage Materials',
      description: 'Upload, edit, and organize study materials for students',
      icon: <FileText className="h-7 w-7 text-white" />,
      href: '/materials',
      color: {
        bg: 'bg-gradient-to-br from-purple-500 to-purple-700',
        iconBg: 'bg-white/20',
        text: 'text-purple-100',
        button: 'bg-white text-purple-600',
        buttonHover: 'hover:bg-purple-50',
      },
    },
    {
      title: 'Manage Events',
      description: 'Create and manage event galleries with photos and videos',
      icon: <Image className="h-7 w-7 text-white" />,
      href: '/events',
      color: {
        bg: 'bg-gradient-to-br from-pink-500 to-rose-600',
        iconBg: 'bg-white/20',
        text: 'text-pink-100',
        button: 'bg-white text-pink-600',
        buttonHover: 'hover:bg-pink-50',
      },
    },
    {
      title: 'Manage Team',
      description: 'Add and update team members and faculty information',
      icon: <Users className="h-7 w-7 text-white" />,
      href: '/team/admin',
      color: {
        bg: 'bg-gradient-to-br from-blue-500 to-blue-700',
        iconBg: 'bg-white/20',
        text: 'text-blue-100',
        button: 'bg-white text-blue-600',
        buttonHover: 'hover:bg-blue-50',
      },
    },
    {
      title: 'Manage Success Stories',
      description: 'Showcase student achievements and testimonials',
      icon: <Award className="h-7 w-7 text-white" />,
      href: '/achievements/admin',
      color: {
        bg: 'bg-gradient-to-br from-amber-500 to-orange-600',
        iconBg: 'bg-white/20',
        text: 'text-amber-100',
        button: 'bg-white text-amber-600',
        buttonHover: 'hover:bg-amber-50',
      },
    },
    {
      title: 'Manage Settings',
      description: 'Configure website settings, updates, and contact info',
      icon: <Settings className="h-7 w-7 text-white" />,
      href: '/settings',
      color: {
        bg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
        iconBg: 'bg-white/20',
        text: 'text-emerald-100',
        button: 'bg-white text-emerald-600',
        buttonHover: 'hover:bg-emerald-50',
      },
    },
    {
      title: 'Manage Users',
      description: 'View and manage registered users and their roles',
      icon: <Shield className="h-7 w-7 text-white" />,
      href: '/user/admin',
      color: {
        bg: 'bg-gradient-to-br from-slate-600 to-slate-800',
        iconBg: 'bg-white/20',
        text: 'text-slate-200',
        button: 'bg-white text-slate-700',
        buttonHover: 'hover:bg-slate-50',
      },
    },
  ];

  return (
    <>
      <SEO
        title="Admin Panel - Tuition Point"
        description="Administrative dashboard and management tools"
      />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-8">
          {/* Welcome Header */}
          <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-500 rounded-xl p-8 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-white">
                  Admin Panel
                </h1>
                <p className="mt-2 text-emerald-100 text-lg">
                  Welcome back, {user.name || user.email.split('@')[0]}! Manage your education center from here.
                </p>
              </div>
              <div className="hidden md:block">
                <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Shield className="h-12 w-12 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Overview</h2>
            {statsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                <span className="ml-2 text-gray-500">Loading stats...</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <Card
                  className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer"
                  onClick={() => navigate('/user/admin')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-xs font-medium">Users</p>
                        <p className="text-2xl font-bold mt-1">{stats.users}</p>
                      </div>
                      <div className="p-2 bg-white/20 rounded-lg">
                        <Users className="h-5 w-5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer"
                  onClick={() => navigate('/materials')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-xs font-medium">Materials</p>
                        <p className="text-2xl font-bold mt-1">{stats.materials}</p>
                      </div>
                      <div className="p-2 bg-white/20 rounded-lg">
                        <BookOpen className="h-5 w-5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="bg-gradient-to-br from-pink-500 to-rose-500 text-white border-0 shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer"
                  onClick={() => navigate('/events')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-pink-100 text-xs font-medium">Events</p>
                        <p className="text-2xl font-bold mt-1">{stats.events}</p>
                      </div>
                      <div className="p-2 bg-white/20 rounded-lg">
                        <Camera className="h-5 w-5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="bg-gradient-to-br from-cyan-500 to-teal-500 text-white border-0 shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer"
                  onClick={() => navigate('/team/admin')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-cyan-100 text-xs font-medium">Team</p>
                        <p className="text-2xl font-bold mt-1">{stats.teamMembers}</p>
                      </div>
                      <div className="p-2 bg-white/20 rounded-lg">
                        <Users className="h-5 w-5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="bg-gradient-to-br from-amber-500 to-orange-500 text-white border-0 shadow-md hover:shadow-xl hover:scale-105 transition-all cursor-pointer"
                  onClick={() => navigate('/achievements/admin')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-xs font-medium">Stories</p>
                        <p className="text-2xl font-bold mt-1">{stats.successStories}</p>
                      </div>
                      <div className="p-2 bg-white/20 rounded-lg">
                        <Award className="h-5 w-5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Management Cards Grid */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {managementItems.map((item) => (
                <ManagementCard key={item.href} {...item} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPage;
